import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Grid,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';

import { Button, WrappedTextInput, Box } from 'components';
import {
  loginUser,
  emptyState,
  loginVerifyCode,
  emptyUserLogin,
} from 'redux-modules/auth/Actions';
import { ISignInModal } from 'common/types';
import {
  signInSchema,
  signInVerificationSchema,
} from 'common/utils/validations';
import { RootState } from 'redux-modules/store/rootState';
import { Logo } from 'assets';
import history from 'common/utils/history';
import { emptyInvestorState } from 'redux-modules/investor/Actions';
import { emptyDealsState } from 'redux-modules/deals/Actions';

import styles from './styles';
import SignInImage from '../SignInImage';

const SignIn = () => {
  const classes = styles();
  const dispatch = useDispatch();

  const {
    isLoading,
    errorMessage,
    verificationCodeEnable,
    tempCognitoUser,
  } = useSelector(({ Auth }: RootState) => Auth);

  const { handleSubmit, control, setValue } = useForm({
    mode: 'onChange',
    resolver: yupResolver(
      !verificationCodeEnable ? signInSchema : signInVerificationSchema,
    ),
  });

  const onSubmit = async (data: ISignInModal) => {
    dispatch(emptyInvestorState());
    dispatch(emptyDealsState());
    dispatch(loginUser(data));
  };

  const onSubmitCode = async (data: any) => {
    const { code } = data;
    dispatch(loginVerifyCode({ user: tempCognitoUser, code }));
    sessionStorage.removeItem('isDashboard');
  };

  useEffect(() => {
    if (errorMessage) {
      dispatch(emptyState());
    }
  }, []);

  return (
    <Box className={classes.signUpContainer}>
      <Box className={classes.gridContainer}>
        <Box className={classes.leftSideBox}>
          {!verificationCodeEnable && (
            <Box className={classes.leftSide}>
              <Box className={classes.imgBox}>
                <img src={Logo} alt={Logo} className={classes.logoIcon} />
              </Box>
              <Typography
                variant="h2"
                gutterBottom
                className={classes.welcome8vdx}
              >
                Login
              </Typography>
              <Typography className={classes.welcomeSub8vdx}>
                Please login to your account.
              </Typography>
              <form
                onSubmit={handleSubmit(onSubmit)}
                data-testid="signInForm"
                className={classes.validateForm}
              >
                <Typography className={classes.labelText} variant="body1">
                  Email *
                </Typography>
                <Controller
                  name="username"
                  control={control}
                  defaultValue=""
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <Box className={classes.controllerBox}>
                      <WrappedTextInput
                        type="search"
                        // label="Email"
                        placeholder="Email"
                        className={classes.textValInput}
                        InputProps={{ classes: { root: classes.inputRoot } }}
                        InputLabelProps={{
                          classes: {
                            root: classes.labelRoot,
                            focused: classes.labelFocused,
                          },
                        }}
                        value={value}
                        onChange={(e: any) =>
                          onChange(e.target.value.trimStart())
                        }
                        error={!!error?.message}
                      />
                      {error?.message && (
                        <span className={classes.errorText}>
                          {error?.message}
                        </span>
                      )}
                    </Box>
                  )}
                  rules={{ required: 'Email required' }}
                />
                <Typography className={classes.labelText} variant="body1">
                  Password *
                </Typography>
                <Controller
                  name="password"
                  control={control}
                  defaultValue=""
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <Box className={classes.controllerBox}>
                      <WrappedTextInput
                        // label="Password"
                        type="password"
                        placeholder="Password"
                        className={classes.textValInput}
                        InputProps={{ classes: { root: classes.inputRoot } }}
                        InputLabelProps={{
                          classes: {
                            root: classes.labelRoot,
                            focused: classes.labelFocused,
                          },
                        }}
                        value={value}
                        onChange={(e: any) =>
                          onChange(e.target.value.trimStart())
                        }
                        error={!!error?.message}
                      />
                      {error?.message && (
                        <span className={classes.errorPwd}>
                          {error?.message}
                        </span>
                      )}
                    </Box>
                  )}
                  rules={{ required: 'Password required' }}
                />
                <Grid container>
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          name="checkedI"
                          color="secondary"
                        />
                      }
                      label="Remember Me"
                    />
                  </Grid>
                  <Grid item xs={6} className={classes.forgotText}>
                    <p
                      className={classes.forgotPassword}
                      onClick={() => history.push('/forgot-password')}
                    >
                      Forgot Password?
                    </p>
                  </Grid>
                </Grid>
                <Box className={classes.signInBtnBox}>
                  {errorMessage && (
                    <Typography
                      variant="body1"
                      gutterBottom
                      className={classes.userNExit}
                    >
                      {errorMessage}
                    </Typography>
                  )}
                  <Button
                    type="submit"
                    className={classes.signupBtn}
                    name="Sign In"
                    isLoading={isLoading}
                    disabled={isLoading}
                  />
                </Box>
              </form>
              <Box>
                <Typography className={classes.alreadyAcText}>
                  Not registered yet ?
                  <a
                    onClick={() => history.push('/sign-up')}
                    rel="noreferrer"
                    className={classes.signInLink}
                  >
                    {' '}
                    Create an Account
                  </a>
                </Typography>
                <Typography className={classes.copyText}>
                  © 2022 8vdX All rights reserved .
                </Typography>
              </Box>
            </Box>
          )}
          {verificationCodeEnable && (
            <Box className={classes.leftSide}>
              <Box className={classes.imgBox}>
                <img src={Logo} alt={Logo} className={classes.logoIcon} />
              </Box>
              <Typography
                variant="h2"
                gutterBottom
                className={classes.welcome8vdx}
              >
                Two-factor Authentication
              </Typography>
              <Typography className={classes.welcomeSub8vdx}>
                Please enter verification code. The verification code is sent to
                your registered email id.
              </Typography>
              <form
                onSubmit={handleSubmit(onSubmitCode)}
                data-testid="signInFormCode"
                className={classes.validateForm}
              >
                <Controller
                  name="code"
                  control={control}
                  defaultValue=""
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <Box className={classes.controllerBox}>
                      <WrappedTextInput
                        type="search"
                        // label="Email"
                        placeholder="Verification Code"
                        className={classes.textValInput}
                        InputProps={{ classes: { root: classes.inputRoot } }}
                        InputLabelProps={{
                          classes: {
                            root: classes.labelRoot,
                            focused: classes.labelFocused,
                          },
                        }}
                        value={value}
                        onChange={(e: any) =>
                          onChange(e.target.value.trimStart())
                        }
                        error={!!error?.message}
                      />
                      {error?.message && (
                        <span className={classes.errorText}>
                          {error?.message}
                        </span>
                      )}
                    </Box>
                  )}
                  rules={{ required: 'Verification code required' }}
                />{' '}
                <Box>
                  {errorMessage && (
                    <Typography
                      variant="body1"
                      gutterBottom
                      className={classes.errorAlready}
                    >
                      {errorMessage}
                    </Typography>
                  )}
                  <Button
                    type="submit"
                    className={classes.verifyBtn}
                    name="Verify"
                    isLoading={isLoading}
                    disabled={isLoading}
                  />
                </Box>
              </form>
              <Box>
                <Typography className={classes.alreadyAcText}>
                  Already have an account ?
                  <a
                    onClick={() => {
                      dispatch(emptyUserLogin());
                      setValue('code', null);
                    }}
                    rel="noreferrer"
                    className={classes.signInLink}
                  >
                    {' '}
                    Back to login
                  </a>
                </Typography>
                <Typography className={classes.copyText}>
                  © 2022 8vdX All rights reserved .
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        <SignInImage />
      </Box>
    </Box>
  );
};

export default SignIn;
